import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FileText, Search, ShieldCheck, Landmark, ArrowRight, MessageSquare,
  Handshake, ClipboardCheck, Bell, ChevronRight, Star, ExternalLink, PenLine, Bookmark,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import PhoneSearch from "@/components/PhoneSearch";
import TransparencyProfile from "@/components/TransparencyProfile";
import Seo, { SITE_URL } from "@/components/Seo";
import { CategorySidebar } from "@/components/discovery/CategorySidebar";
import DisputeCard from "@/components/DisputeCard";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const STEPS = [
  { icon: ClipboardCheck, title: "Document the issue", desc: "Record facts, amounts, dates and upload supporting evidence privately." },
  { icon: MessageSquare, title: "Give the other party a chance", desc: "The responding party is notified and gets a fair opportunity to reply." },
  { icon: Handshake, title: "Resolve, record & close fairly", desc: "Negotiate, agree a settlement, and keep a neutral audit record." },
];

const FAQS = [
  ["Is a complaint a proof of wrongdoing?", "No. Every complaint is an allegation until it is verified or mutually resolved. The other party always gets a chance to respond."],
  ["Will my mobile number be shown publicly?", "No. Numbers are masked (e.g. ******6990). Full numbers are never displayed publicly."],
  ["Does ComplaintAdvisor recover my money?", "No. We are a neutral technology platform for documentation and communication. We do not guarantee any outcome."],
  ["Can I remove a legitimate complaint by paying?", "No. Removal of legitimate complaints cannot be purchased. Corrections and appeals follow a moderation process."],
];

const STATUS_OPTS = [
  ["all", "All statuses"], ["complaint_filed", "Complaint Filed"], ["response_pending", "Response Pending"],
  ["under_discussion", "Under Discussion"], ["resolution_proposed", "Resolution Proposed"],
  ["resolved_by_agreement", "Resolved by Agreement"], ["unresolved", "Unresolved"], ["no_response", "No Response"],
];

export default function Home() {
  const navigate = useNavigate();
  const { user, openAuth, setShowPartyPick } = useAuth();
  const [result, setResult] = useState(null);
  const [feed, setFeed] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [gov, setGov] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [discCats, setDiscCats] = useState([]);
  const [cat, setCat] = useState("all");
  const [city, setCity] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => {
    api.get("/complaints/public").then((r) => setFeed(r.data)).catch(() => {});
    api.get("/vendors/top").then((r) => setVendors(r.data)).catch(() => {});
    api.get("/government-resources").then((r) => setGov(r.data)).catch(() => {});
    api.get("/updates").then((r) => setUpdates(r.data)).catch(() => {});
    api.get("/discovery/categories").then((r) => setDiscCats(r.data)).catch(() => {});
  }, []);

  const cats = useMemo(() => Array.from(new Set(feed.flatMap((i) => i.categories || []))).sort(), [feed]);
  const cities = useMemo(() => Array.from(new Set(feed.map((i) => i.city).filter(Boolean))).sort(), [feed]);
  const filtered = feed.filter((i) => {
    if (cat !== "all" && !(i.categories || []).includes(cat)) return false;
    if (city !== "all" && i.city !== city) return false;
    if (status !== "all" && i.status !== status) return false;
    return true;
  });

  const startComplaint = () => (user ? setShowPartyPick(true) : openAuth("signup"));
  const onChallenge = () => (user ? toast.info("Open the complaint from your dashboard to respond or challenge.") : openAuth("login"));
  const saveHomeSearch = async () => {
    if (!user) return openAuth("login");
    const payload = { category: cat !== "all" ? cat : null, city: city !== "all" ? city : null, status: status !== "all" ? status : null };
    if (!payload.category && !payload.city && !payload.status) return toast.error("Pick a category, city or status to save");
    try {
      await api.post("/saved-searches", { ...payload, label: [payload.category, payload.city, STATUS_OPTS.find((s) => s[0] === payload.status)?.[1]].filter(Boolean).join(" · ") });
      toast.success("Search saved — you'll be alerted when new disputes match");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };

  return (
    <div>
      <Seo
        title="ComplaintAdvisor | Consumer Complaints & Dispute Resolution"
        description="Check service providers review, file feedback against customer, communicate with the other party, and work toward dispute resolution with ComplaintAdvisor."
        path="/"
        jsonld={[
          { "@context": "https://schema.org", "@type": "Organization", name: "ComplaintAdvisor", url: SITE_URL,
            description: "A neutral customer-and-vendor information and dispute-support platform." },
          { "@context": "https://schema.org", "@type": "WebSite", name: "ComplaintAdvisor", url: SITE_URL },
        ]}
      />
      {/* Government updates strip */}
      <div className="bg-brand-navy text-white overflow-hidden border-b border-white/10">
        <div className="ca-container flex items-center gap-3 py-2">
          <span className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-teal shrink-0"><Bell className="h-3.5 w-3.5" /> Updates</span>
          <div className="overflow-hidden flex-1">
            <div className="ca-marquee">
              {[...updates, ...updates].map((u, i) => (
                <a key={i} href={u.link} target="_blank" rel="noreferrer" className="mx-6 text-xs text-slate-300 hover:text-teal inline-flex items-center gap-1.5">
                  <span className="text-teal font-semibold">{u.source}</span>
                  <span className="text-slate-500">{u.date}</span>· {u.headline}
                  <span className="italic text-slate-500">(General information only)</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Hero heading */}
      <section className="bg-gradient-to-b from-white to-[#F8FAFC] border-b border-slate-100">
        <div className="ca-container py-8 lg:py-10 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-brand/10 text-teal px-3 py-1 text-xs font-semibold mb-3">
            <ShieldCheck className="h-3.5 w-3.5" /> Neutral · Consent-based · Fair
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-extrabold tracking-tight text-slate-900 leading-[1.08]">
            Unfair Service or Payment Dispute? <span className="text-teal">Take the First Step.</span>
          </h1>
          <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
            Review verified dispute information, document your experience, communicate fairly, and resolve customer–vendor issues before they become expensive legal disputes.
          </p>
        </div>
      </section>

      {/* File a Complaint section (dark) + Check Before Hiring (light) */}
      <section className="ca-container py-8 grid md:grid-cols-2 gap-5">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="rounded-2xl p-6 bg-brand-navy text-white" data-testid="card-file-complaint">
          <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center mb-4"><FileText className="h-6 w-6 text-teal" /></div>
          <h3 className="font-display text-xl font-bold">File a Complaint</h3>
          <p className="text-slate-300 text-sm mt-2 mb-4">Have a Complaint? Raise it. Document it. Resolve it.</p>
          <Button onClick={startComplaint} className="bg-teal-brand hover:bg-teal-700 text-white" data-testid="start-complaint-btn">
            Start Complaint <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}
          className="rounded-2xl p-6 bg-brand-navy text-white" data-testid="card-check-hiring">
          <div className="h-11 w-11 rounded-xl bg-white/10 flex items-center justify-center mb-4"><Search className="h-6 w-6 text-teal" /></div>
          <h3 className="font-display text-xl font-bold">Check Before Hiring</h3>
          <p className="text-slate-300 text-sm mt-2 mb-4">Hiring Someone New? Check customer or vendor reviews before you trust.</p>
          <Button onClick={() => navigate("/check-before-hiring")} variant="secondary" data-testid="check-number-btn">
            Check Reviews & Feedback <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </motion.div>
      </section>

      {/* Browse service providers by category — with sticky discovery sidebar */}
      <section className="ca-container py-6">
        <div className="lg:grid lg:grid-cols-[250px_minmax(0,1fr)] lg:gap-6 lg:items-start">
          <aside className="hidden lg:block lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto" data-testid="home-category-sidebar">
            <CategorySidebar categories={discCats} />
          </aside>
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-xl font-bold text-slate-900">Browse Service Providers by Category</h2>
              <Link to="/vendors" className="text-sm text-teal font-medium flex items-center gap-1">View all <ChevronRight className="h-4 w-4" /></Link>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {discCats.slice(0, 12).map((c) => (
                <Link key={c.slug} to={`/vendors/${c.slug}`} data-testid={`home-cat-tile-${c.slug}`}
                  className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-teal-brand/40 hover:shadow-sm transition-all">
                  <p className="font-semibold text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">{c.intro}</p>
                  <p className="text-[11px] text-teal mt-2">{c.subcategories?.length || 0} subcategories &rarr;</p>
                </Link>
              ))}
            </div>
            {/* Mobile: category + subcategory list (sidebar hidden on small screens) */}
            <div className="lg:hidden mt-4">
              <CategorySidebar categories={discCats} />
            </div>
          </div>
        </div>
      </section>

      {/* Mobile number finder section */}
      <section className="ca-container pb-4">
        <div className="rounded-2xl bg-brand-navy p-6 sm:p-8">
          <h2 className="text-lg sm:text-xl font-display font-bold text-white text-center">Check a mobile number before you trust</h2>
          <p className="text-slate-300 text-sm text-center mt-1 mb-5 max-w-xl mx-auto">Entering a number does not reveal private information, and results do not establish guilt.</p>
          <div className="max-w-3xl mx-auto"><PhoneSearch onResult={setResult} /></div>
        </div>
        {result && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-6 ca-fade-up"><TransparencyProfile profile={result} /></div>
        )}
      </section>

      {/* Trust steps */}
      <section className="ca-container py-6 grid md:grid-cols-3 gap-4">
        {STEPS.map((s, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-3 mb-2">
              <span className="h-7 w-7 rounded-full bg-teal-brand text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
              <s.icon className="h-5 w-5 text-teal" />
            </div>
            <h4 className="font-semibold text-slate-900 text-sm">{s.title}</h4>
            <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
          </div>
        ))}
      </section>

      {/* 3-column: gov | feed | vendors — sidebars frozen (sticky) */}
      <section className="ca-container py-6 grid lg:grid-cols-4 gap-6 items-start">
        {/* Left: Government (sticky) */}
        <aside className="lg:col-span-1 space-y-3 order-2 lg:order-1 lg:sticky lg:top-20 self-start lg:max-h-[calc(100vh-5.5rem)] lg:overflow-y-auto pr-1">
          <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Landmark className="h-5 w-5 text-teal" /> Consumer Help & Guidelines</h3>
          {gov.slice(0, 4).map((g) => (
            <a key={g.id} href={g.link} target="_blank" rel="noreferrer" className="block rounded-xl border border-slate-200 bg-white p-3 hover:border-teal-brand/40 transition-colors">
              <p className="text-sm font-semibold text-slate-800 flex items-center gap-1">{g.title} <ExternalLink className="h-3 w-3 text-slate-400" /></p>
              <p className="text-xs text-slate-500 mt-1">{g.desc}</p>
              <p className="text-[10px] text-slate-400 mt-1 italic">General information only · {g.source}</p>
            </a>
          ))}
          <Link to="/government-guidelines" className="text-sm text-teal font-medium flex items-center gap-1">View all <ChevronRight className="h-4 w-4" /></Link>
        </aside>

        {/* Center: Feed */}
        <div className="lg:col-span-2 order-1 lg:order-2 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl font-bold text-slate-900">Community Dispute Feed</h3>
            <Link to="/disputes" className="text-sm text-teal font-medium">Open feed</Link>
          </div>

          {/* Composer */}
          <button onClick={startComplaint} data-testid="feed-composer" className="w-full rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3 text-left hover:border-teal-brand/40 transition-colors">
            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center"><PenLine className="h-5 w-5 text-slate-400" /></div>
            <span className="text-slate-500 text-sm flex-1">Have a service or payment dispute? Document it fairly…</span>
            <span className="rounded-full bg-teal-brand text-white text-sm font-semibold px-4 py-2">Start</span>
          </button>

          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="home-filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="max-h-64"><SelectItem value="all">All categories</SelectItem>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={city} onValueChange={setCity}>
              <SelectTrigger data-testid="home-filter-city"><SelectValue placeholder="City" /></SelectTrigger>
              <SelectContent className="max-h-64"><SelectItem value="all">All cities</SelectItem>{cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="home-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>{STATUS_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-slate-400">{filtered.length} of {feed.length} disputes</p>
            <Button variant="outline" size="sm" onClick={saveHomeSearch} data-testid="home-save-search"><Bookmark className="h-4 w-4 mr-1" /> Save this search</Button>
          </div>

          <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-500">
            Anonymised, moderated records. Full numbers, addresses, documents and private messages are never shown. Complaints are allegations until verified or mutually resolved.
          </div>

          {filtered.map((c) => <DisputeCard key={c.id} c={c} onChallenge={onChallenge} />)}
          {filtered.length === 0 && <p className="text-sm text-slate-500 py-8 text-center">No disputes match these filters.</p>}
        </div>

        {/* Right: verified vendors (sticky) */}
        <aside className="lg:col-span-1 space-y-3 order-3 lg:sticky lg:top-20 self-start lg:max-h-[calc(100vh-5.5rem)] lg:overflow-y-auto pr-1">
          <h3 className="font-display font-bold text-slate-900 flex items-center gap-2"><Star className="h-5 w-5 text-teal" /> Top Verified Vendors</h3>
          {vendors.slice(0, 6).map((v) => (
            <div key={v.id} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-800">{v.name}</p>
                {v.sponsored && <span className="text-[9px] font-bold uppercase text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Sponsored</span>}
              </div>
              <p className="text-xs text-slate-500">{v.category} · {v.city}</p>
              <div className="flex items-center gap-2 mt-1.5 text-[11px] text-slate-500">
                {v.verified && <span className="text-teal font-medium">✓ Verified</span>}
                <span>{v.response_rate}% response</span>
                <span>{v.resolution_count} resolved</span>
              </div>
            </div>
          ))}
        </aside>
      </section>

      {/* FAQ */}
      <section className="ca-container py-10">
        <h3 className="font-display text-2xl font-bold text-slate-900 mb-4">Frequently asked questions</h3>
        <Accordion type="single" collapsible className="rounded-2xl border border-slate-200 bg-white px-4">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left text-slate-800">{q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="ca-container pb-16">
        <div className="rounded-2xl bg-brand-navy text-white p-8 text-center">
          <h3 className="font-display text-2xl sm:text-3xl font-bold">Have a dispute to resolve?</h3>
          <p className="text-slate-300 mt-2 max-w-2xl mx-auto text-sm">
            Document your experience, notify the other party, and work toward a fair resolution — all on a neutral, privacy-first platform. Serious safety threats or crimes should be reported to the appropriate authorities.
          </p>
          <div className="mt-5 flex flex-wrap gap-3 justify-center">
            <Button onClick={startComplaint} className="bg-teal-brand hover:bg-teal-700 text-white">Start a Complaint</Button>
            <Button onClick={() => navigate("/contact")} variant="secondary">Contact Us</Button>
          </div>
        </div>
      </section>
    </div>
  );
}
